
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface FamilyActivityStatus {
  isActiveFamily: boolean;
  familyRecipeCount: number;
  familyMealPlanCount: number;
  shouldShowFamilyFeatures: boolean;
  shouldUnlockMealPlanning: boolean;
}

type UserContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isPasswordRecovery: boolean;
  familyActivity: FamilyActivityStatus | null;
};

const defaultFamilyActivity: FamilyActivityStatus = {
  isActiveFamily: false,
  familyRecipeCount: 0,
  familyMealPlanCount: 0,
  shouldShowFamilyFeatures: false,
  shouldUnlockMealPlanning: false,
};

// Create context with default values
const UserContext = createContext<UserContextType>({
  user: null,
  session: null,
  isLoading: true,
  isAuthenticated: false,
  isPasswordRecovery: false,
  familyActivity: null,
});

// Provider component
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState<boolean>(false);
  const [familyActivity, setFamilyActivity] = useState<FamilyActivityStatus | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;
    
    const initializeAuth = async () => {
      try {
        // Set up auth subscription - this handles all auth state changes
        const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
          console.log('🔐 Auth state changed:', event, {
            session_exists: !!currentSession,
            user_id: currentSession?.user?.id,
            user_email: currentSession?.user?.email,
            session_token_type: currentSession?.token_type,
            expires_at: currentSession?.expires_at
          });
          
          // Update auth state immediately
          setSession(currentSession);
          setUser(currentSession?.user || null);
          setIsLoading(false);
          
          // Handle password recovery event
          if (event === 'PASSWORD_RECOVERY') {
            console.log('🔐 Password recovery detected');
            setIsPasswordRecovery(true);
          }
          
          // Handle sign out cleanup
          if (event === 'SIGNED_OUT') {
            console.log('🔐 Handling sign out cleanup');
            setFamilyActivity(null);
            setIsPasswordRecovery(false);
            
            // Clear all query cache to prevent data leakage between users
            console.log('🔐 Clearing query cache to ensure fresh data on next sign-in');
            queryClient.clear();
          }
          
          // Set default family activity when signing in
          if (event === 'SIGNED_IN') {
            console.log('🔐 Handling sign in - setting family activity');
            setFamilyActivity(defaultFamilyActivity);
          }
        });
        
        subscription = authSubscription;
        
        // Check for existing session on startup
        console.log('🔐 Checking for existing session on startup...');
        const { data: { session: existingSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('🔐 Error getting session:', sessionError);
        } else if (existingSession) {
          console.log('🔐 Found existing session on startup:', {
            user_id: existingSession.user.id,
            user_email: existingSession.user.email,
            expires_at: existingSession.expires_at
          });
          setSession(existingSession);
          setUser(existingSession.user);
          setFamilyActivity(defaultFamilyActivity);
        } else {
          console.log('🔐 No existing session found on startup');
        }
        
        setIsLoading(false);
        
      } catch (error) {
        console.error('Error initializing auth:', error);
        setSession(null);
        setUser(null);
        setFamilyActivity(null);
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Cleanup function
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []); // Empty dependency array ensures this only runs once

  const value = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    isPasswordRecovery,
    familyActivity,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook for using the context
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
